import type { ShellSidebarSection } from '@orrekie/ui';

import { db, doc, getDoc, useAuth } from '@orrekie/shared';
import { Briefcase, Calculator, FolderPlus, LayoutGrid, Settings, User } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { useWorkspace } from '../context/WorkspaceContext';
import { enableWorkspaceSubstrateRoutes } from '../lib/productCapabilities';
import type { ProductTierAudience } from '../lib/productTier';

import { useProductTier } from './useProductTier';

const WORKBENCH_ORG_ROLES = new Set(['org_user', 'org_admin', 'org_owner']);

/**
 * App-shell audience: honours explicit `free` tier; org-role personas without tier keep
 * workbench navigation (calculator route uses {@link resolveCalculatorShellAudience}).
 */
export function resolveAppShellAudience(
  tierAudience: ProductTierAudience,
  /** Raw `users/{uid}.productTier` after load (`null` = field missing). */
  profileTierDoc: string | null | undefined,
  role: string | null | undefined,
  systemRole: string | null | undefined,
): 'free' | 'workbench' {
  if (profileTierDoc?.trim().toLowerCase() === 'free') return 'free';
  if (tierAudience !== 'free') return 'workbench';
  const hasWorkbenchOrgRole =
    WORKBENCH_ORG_ROLES.has(role ?? '') || WORKBENCH_ORG_ROLES.has(systemRole ?? '');
  return hasWorkbenchOrgRole ? 'workbench' : 'free';
}

/** App-shell tier: explicit free stays free; org roles without tier keep workbench navigation. */
export function useAppShellAudience(): 'free' | 'workbench' {
  const { role, systemRole, hasUser, user, ready: authReady } = useAuth();
  const { audience } = useProductTier();
  const [profileTierDoc, setProfileTierDoc] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!authReady) {
      setProfileTierDoc(undefined);
      return;
    }

    if (!hasUser || !user?.uid) {
      setProfileTierDoc(null);
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const snap = await getDoc(doc(db(), 'users', user.uid));
        if (!cancelled) {
          const raw = snap.data()?.productTier;
          setProfileTierDoc(typeof raw === 'string' ? raw : null);
        }
      } catch {
        if (!cancelled) setProfileTierDoc(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authReady, hasUser, user?.uid]);

  return useMemo(
    () => resolveAppShellAudience(audience, profileTierDoc, role, systemRole),
    [audience, profileTierDoc, role, systemRole],
  );
}

const s = 18;
const Ic = { Briefcase, Calculator, FolderPlus, LayoutGrid, Settings, User };

export function useUserShellSidebar(tierAudience: ProductTierAudience = 'workbench'): ShellSidebarSection[] {
  const { recentProjects } = useWorkspace();
  return useMemo(() => {
    const B = Ic.Briefcase;
    const calculatorItem = {
      id: 'u-value-calculator',
      label: 'U-Value Calculator',
      icon: <Ic.Calculator size={s} />,
      to: '/u-value/calculator',
    };
    const acct: ShellSidebarSection = {
      id: 'account',
      label: 'Account',
      items:
        tierAudience === 'free'
          ? [{ id: 'settings', label: 'Settings', icon: <Ic.Settings size={s} />, to: '/settings' }]
          : [
              { id: 'profile', label: 'Profile', icon: <Ic.User size={s} />, to: '/profile' },
              { id: 'settings', label: 'Settings', icon: <Ic.Settings size={s} />, to: '/settings' },
            ],
    };

    if (tierAudience === 'free') {
      const freeTools: ShellSidebarSection = {
        id: 'tools',
        label: 'Tools',
        items: [calculatorItem],
      };
      return [freeTools, acct];
    }

    if (!enableWorkspaceSubstrateRoutes()) {
      return [acct];
    }

    const work: ShellSidebarSection = {
      id: 'work',
      label: 'Work',
      items: [
        { id: 'nav-sandbox', label: 'Sandbox', icon: <Ic.LayoutGrid size={s} />, to: '/sandbox' },
        { id: 'nav-projects', label: 'Projects', icon: <B size={s} />, to: '/workspace#projects' },
        {
          id: 'workspace-create-project',
          label: 'Create project',
          icon: <Ic.FolderPlus size={s} />,
          to: '/projects/new',
        },
        ...recentProjects.map((p) => ({
          id: `workspace-project-${p.id}`,
          label: p.label,
          icon: <B size={s} />,
          to: `/projects/${encodeURIComponent(p.id)}`,
        })),
      ],
    };
    return [work, acct];
  }, [recentProjects, tierAudience]);
}
